import { zodResolver } from '@hookform/resolvers/zod';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid,
    IconButton, Switch, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { ReactNode, useEffect } from 'react';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { smallDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { CustomFieldService } from '../services/custom-field.service';
import { CustomFieldRequest } from '../services/requests/custom-field.request';
import { CustomFieldResponse, ECustomFieldType } from '../services/responses/custom-field.response';
import { TYPE_KEY } from '../custom-field-value';
import { firstErrorMessage } from '@functions/first-error-message';

interface Props {
    open: boolean;
    edit: CustomFieldResponse | null;
    handleModal: () => void;
    getAll: () => void;
    clearSearch: () => void;
}

/// As opções são objeto só no formulário: o useFieldArray precisa disso para acompanhar cada linha.
interface FormValues {
    name: string;
    type: ECustomFieldType;
    required: boolean;
    active: boolean;
    sortOrder: string;
    options: { value: string }[];
}

const EMPTY: FormValues = {
    name: '',
    type: ECustomFieldType.Text,
    required: false,
    active: true,
    sortOrder: '',
    options: [],
};

export const CustomFieldModal = ({ open, edit, handleModal, getAll, clearSearch }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();
    const service = new CustomFieldService();

    const schema = z
        .object({
            name: z
                .string()
                .trim()
                .min(1, t('camposPersonalizados.nomeObrigatorio'))
                .max(60, t('camposPersonalizados.nomeLongo')),
            type: z.nativeEnum(ECustomFieldType),
            required: z.boolean(),
            active: z.boolean(),
            sortOrder: z.string().regex(/^\d*$/, t('camposPersonalizados.ordemInvalida')),
            options: z.array(
                z.object({
                    value: z
                        .string()
                        .trim()
                        .min(1, t('camposPersonalizados.opcaoVazia'))
                        .max(100, t('camposPersonalizados.opcaoLonga')),
                })
            ),
        })
        .superRefine((data, ctx) => {
            if (data.type !== ECustomFieldType.List) return;

            // "root" é onde o react-hook-form guarda o erro da lista inteira, não de cada linha.
            if (data.options.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['options', 'root'],
                    message: t('camposPersonalizados.opcoesObrigatorias'),
                });
                return;
            }

            const normalized = data.options.map((option) => option.value.trim().toLowerCase());

            if (new Set(normalized).size !== normalized.length) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['options', 'root'],
                    message: t('camposPersonalizados.opcoesRepetidas'),
                });
            }
        });

    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: EMPTY,
    });

    const { control, handleSubmit, reset, watch, formState: { errors } } = form;
    const { fields: options, append, remove } = useFieldArray({ control, name: 'options' });
    const type = watch('type');

    useEffect(() => {
        if (!open) return;

        reset(
            edit
                ? {
                      name: edit.name,
                      type: edit.type,
                      required: edit.required,
                      active: edit.active,
                      sortOrder: String(edit.sortOrder),
                      options: edit.options.map((value) => ({ value })),
                  }
                : EMPTY
        );
    }, [open]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);
        }
    }, [errors]);

    const toRequest = (data: FormValues): CustomFieldRequest => ({
        name: data.name.trim(),
        type: data.type,
        required: data.required,
        active: data.active,
        sortOrder: data.sortOrder === '' ? null : Number(data.sortOrder),
        options: data.type === ECustomFieldType.List ? data.options.map((option) => option.value.trim()) : [],
    });

    const saveChanges = async (data: FormValues) => {
        setLoading(dispatch, true);

        try {
            if (edit) {
                await service.update(edit.id, toRequest(data));
                showAlert(t('general.atualizacao'), 'success', 2500);
                getAll();
            } else {
                await service.create(toRequest(data));
                showAlert(t('general.cadastro'), 'success', 2500);
                clearSearch();
            }

            handleModal();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    const toggle = (name: 'required' | 'active', label: string, help: string): ReactNode => (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <FormControlLabel
                    control={
                        <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            color="success"
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body2">{label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {help}
                            </Typography>
                        </Box>
                    }
                />
            )}
        />
    );

    return (
        <Dialog open={open} onClose={handleModal} fullWidth maxWidth="sm" scroll="paper">
            <DialogTitle>{edit ? t('general.editar') : t('general.adicionar')}</DialogTitle>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(saveChanges)}>
                    <DialogContent style={smallDialog}>
                        <Grid container spacing={2}>
                            <Form.Field size={{ xs: 12, md: 8 }}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('general.nome')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.Input
                                        type="text"
                                        autoComplete="off"
                                        size="medium"
                                        name="name"
                                        autoFocus
                                    />
                                    <Form.ErrorMessage field="name" />
                                </FormControl>
                            </Form.Field>

                            <Form.Field size={{ xs: 12, md: 4 }}>
                                <FormControl fullWidth>
                                    <Form.Label>{t('camposPersonalizados.ordem')}</Form.Label>
                                    <Form.Input
                                        type="number"
                                        autoComplete="off"
                                        size="medium"
                                        name="sortOrder"
                                        placeholder={t('camposPersonalizados.ordemNoFim')}
                                        inputProps={{ min: 0 }}
                                    />
                                    <Form.ErrorMessage field="sortOrder" />
                                </FormControl>
                            </Form.Field>

                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('camposPersonalizados.tipo')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.AutoCompleteSelect
                                        name="type"
                                        options={Object.values(ECustomFieldType)
                                            .filter((value): value is ECustomFieldType => typeof value === 'number')
                                            .map((value) => ({ value, label: t(TYPE_KEY[value]) }))}
                                    />
                                    <Form.ErrorMessage field="type" />
                                </FormControl>
                            </Form.Field>

                            {type === ECustomFieldType.List && (
                                <Form.Field size={12}>
                                    <Form.Label>
                                        {t('camposPersonalizados.opcoes')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                        {options.map((option, index) => (
                                            <Box key={option.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Form.Input
                                                        fullWidth
                                                        type="text"
                                                        autoComplete="off"
                                                        name={`options.${index}.value`}
                                                    />
                                                    <Tooltip title={t('camposPersonalizados.removerOpcao')}>
                                                        <IconButton size="small" onClick={() => remove(index)}>
                                                            <DeleteOutlineIcon fontSize="small" color="error" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                                <Form.ErrorMessage field={`options.${index}.value`} />
                                            </Box>
                                        ))}
                                        <Box>
                                            <MDButton
                                                size="small"
                                                variant="outlined"
                                                color="info"
                                                startIcon={<AddIcon />}
                                                onClick={() => append({ value: '' })}
                                            >
                                                {t('camposPersonalizados.adicionarOpcao')}
                                            </MDButton>
                                        </Box>
                                        <Form.ErrorMessage field="options.root" />
                                        {edit && (
                                            <Typography variant="caption" color="text.secondary">
                                                {t('camposPersonalizados.opcoesAjuda')}
                                            </Typography>
                                        )}
                                    </Box>
                                </Form.Field>
                            )}

                            <Form.Field size={12}>
                                {toggle(
                                    'required',
                                    t('camposPersonalizados.obrigatorio'),
                                    t('camposPersonalizados.obrigatorioAjuda')
                                )}
                            </Form.Field>

                            <Form.Field size={12}>
                                {toggle('active', t('general.ativo'), t('camposPersonalizados.ativoAjuda'))}
                            </Form.Field>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <MDButton
                            sx={{ marginRight: '5px' }}
                            variant="gradient"
                            color="dark"
                            onClick={handleModal}
                        >
                            {t('general.cancelar')}
                        </MDButton>
                        <MDButton variant="gradient" color="success" type="submit">
                            {t('general.salvar')}
                        </MDButton>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
